/**
 * Gallery column counts per breakpoint — the single source for the grid template
 * (`Gallery`) and for everything that reasons in columns (value-prop placement,
 * pagination), so the two can't drift.
 */
export type GalleryColumnsByBreakpoint = Readonly<Record<'_' | 'sm2' | 'md3' | 'lg' | 'xl', number>>;

export const GALLERY_COLUMNS: GalleryColumnsByBreakpoint = { _: 2, sm2: 3, md3: 4, lg: 5, xl: 6 };

/**
 * With the filter rail open beside the grid the grid loses ~300px, so
 * each breakpoint drops one column; tiles then stay ≥ ~265px wide. Below ~235px a card tile's
 * content (9:10 frame + title + swatches) outgrows its 3.15:4.5 box and the swatch row ends up
 * flush against the next row (Arun, 2026-10-15: "these pickers are too close"). Mobile has
 * no rail, so `_` is unchanged.
 */
export const RAIL_OPEN_GALLERY_COLUMNS: GalleryColumnsByBreakpoint = { _: 2, sm2: 2, md3: 3, lg: 4, xl: 5 };

export const GALLERY_TILE_GAP_PX = 3;

/**
 * Fixed `calc()` tracks, NOT `1fr`/`minmax(0, 1fr)`. A flexible `fr` track is sized by an
 * algorithm that still consults item content during layout, and in Safari a fast zoom
 * (overshoot past ~120% then snap back) makes the nested `aspect-ratio` tiles + the JS card
 * scaler transiently inflate a column; the wrong width then latches until reload. A track
 * defined as a fixed length — `(100% - total gap) / N` — is resolved purely from the
 * container width and can never be influenced by content, so columns stay exactly equal
 * through any zoom.
 */
export const gridTemplateColumnsFor = (columns: GalleryColumnsByBreakpoint): Record<keyof GalleryColumnsByBreakpoint, string> => {
  const track = (n: number) => `repeat(${n}, calc((100% - ${GALLERY_TILE_GAP_PX * (n - 1)}px) / ${n}))`;
  return { _: track(columns._), sm2: track(columns.sm2), md3: track(columns.md3), lg: track(columns.lg), xl: track(columns.xl) };
};
